// Centralised browser-notification + sound manager.
//
// Why a service: every page (chat, navbar polling, dashboard) needs to fire
// alerts in the same way. Keeping it in one file means the user only has to
// grant permission once and we share the WebAudio context.
//
// Public API:
//   notif.requestPermission()             → asks for browser-notification access
//   notif.notify({ type, title, body, url })
//        Plays a sound + (if tab is hidden) pops a browser notification.
//        `type` selects the sound: 'task' | 'meeting' | 'message' | 'call' | 'default'
//   notif.markSeen(key, value)            → remember a high-watermark so we
//                                            don't re-notify after a reload
//   notif.getSeen(key)
//   notif.isMuted() / setMuted(bool)     → user can silence sounds entirely
//
// Persistent state (lastSeen + muted flag) is kept in localStorage so
// notifications survive a page refresh.

const LS_LAST_SEEN = 'notif.lastSeen.v1';
const LS_MUTED = 'notif.muted.v1';

class NotificationManager {
    constructor() {
        this.audioContext = null;
        try {
            this.lastSeen = JSON.parse(localStorage.getItem(LS_LAST_SEEN) || '{}');
        } catch {
            this.lastSeen = {};
        }
        this.muted = localStorage.getItem(LS_MUTED) === '1';
    }

    // -----------------------------------------------------------------------
    // PERMISSION
    // -----------------------------------------------------------------------
    permission() {
        if (typeof Notification === 'undefined') return 'unsupported';
        return Notification.permission;
    }

    async requestPermission() {
        if (typeof Notification === 'undefined') return 'unsupported';
        if (Notification.permission === 'granted') return 'granted';
        if (Notification.permission === 'denied') return 'denied';
        try {
            const result = await Notification.requestPermission();
            return result;
        } catch {
            return 'denied';
        }
    }

    // -----------------------------------------------------------------------
    // MUTE TOGGLE — user-controlled, separate from browser permission.
    // Useful for "do not disturb" without revoking permission entirely.
    // -----------------------------------------------------------------------
    isMuted()   { return this.muted; }
    setMuted(v) {
        this.muted = !!v;
        try { localStorage.setItem(LS_MUTED, v ? '1' : '0'); } catch { /* ignore */ }
    }

    // -----------------------------------------------------------------------
    // SOUND — generated with WebAudio so we don't bundle any audio assets.
    // Different `type`s produce slightly different tones, like WhatsApp's
    // double-tap for messages vs the long ping for calls.
    // -----------------------------------------------------------------------
    _ensureAudio() {
        if (this.audioContext) return this.audioContext;
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return null;
        try {
            this.audioContext = new Ctor();
        } catch {
            this.audioContext = null;
        }
        return this.audioContext;
    }

    _beep({ freq = 700, duration = 0.18, volume = 0.15, delay = 0 }) {
        const ctx = this._ensureAudio();
        if (!ctx) return;
        const start = ctx.currentTime + delay;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(volume, start + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + duration + 0.02);
    }

    playSound(type = 'default') {
        if (this.muted) return;
        switch (type) {
            case 'message':
                // Two quick pings, ascending — WhatsApp-style notification
                this._beep({ freq: 660, duration: 0.12, delay: 0 });
                this._beep({ freq: 880, duration: 0.16, delay: 0.13 });
                break;
            case 'call':
                // Three-tone ring, more attention-grabbing
                this._beep({ freq: 880, duration: 0.18, delay: 0 });
                this._beep({ freq: 1100, duration: 0.18, delay: 0.20 });
                this._beep({ freq: 880, duration: 0.22, delay: 0.40 });
                break;
            case 'task':
                // Single low-mid ding
                this._beep({ freq: 560, duration: 0.22, volume: 0.18 });
                break;
            case 'meeting':
                // Single bright bell
                this._beep({ freq: 980, duration: 0.28, volume: 0.18 });
                break;
            case 'meeting-soon':
                // Urgent triple bell — meeting starts within 5 minutes
                this._beep({ freq: 980, duration: 0.15, delay: 0 });
                this._beep({ freq: 980, duration: 0.15, delay: 0.18 });
                this._beep({ freq: 1200, duration: 0.25, delay: 0.36 });
                break;
            default:
                this._beep({ freq: 700, duration: 0.22 });
        }
    }

    // -----------------------------------------------------------------------
    // SYSTEM NOTIFICATION — only fires when the tab is hidden (so we don't
    // distract a user who's already looking at the portal). Always plays
    // the sound regardless of tab visibility.
    // -----------------------------------------------------------------------
    notify({ type = 'default', title, body, icon, url, tag, forceVisible = false }) {
        this.playSound(type);
        if (typeof Notification === 'undefined') return;
        if (Notification.permission !== 'granted') return;
        // Skip the system popup when the user is already on this tab — the
        // sound + any in-app UI is enough. Pass forceVisible: true to override.
        if (!forceVisible && !document.hidden) return;

        try {
            const n = new Notification(title || 'AVANI Workforce OS', {
                body: body || '',
                icon: icon || '/logo.png',
                tag: tag || type,
                renotify: true,
                badge: '/logo.png'
            });
            n.onclick = () => {
                window.focus();
                if (url) {
                    // If a URL was provided, navigate within the SPA. Use
                    // hashchange-friendly path so React Router picks it up.
                    if (window.location.pathname !== url) {
                        window.location.href = url;
                    }
                }
                try { n.close(); } catch { /* ignore */ }
            };
        } catch (e) {
            // Some browsers (Safari < 16) throw on construct; non-fatal.
            console.warn('[notif] system notification failed:', e.message);
        }
    }

    // -----------------------------------------------------------------------
    // LAST-SEEN tracker — used by callers to avoid re-firing for items they
    // already alerted on. Survives page reloads via localStorage.
    // -----------------------------------------------------------------------
    markSeen(key, value) {
        this.lastSeen[key] = value;
        try { localStorage.setItem(LS_LAST_SEEN, JSON.stringify(this.lastSeen)); } catch { /* ignore */ }
    }

    getSeen(key) { return this.lastSeen[key]; }
}

const notif = new NotificationManager();
export default notif;
